import type { Dispatch, SetStateAction } from 'react';
import type { GridStackWidget } from 'gridstack';
import { projectSlice } from '../../redux/ProjectSlice';
import type { AppDispatch } from '../../redux/Store';
import { mergeDashboardWidgetLayouts, parseDashboardWidgetContent } from '../../utils/sensor-stream-tracker';
import type { DashboardWidgetContent } from './dashboardTypes';

type LayoutMetaSync = (layout: GridStackWidget[]) => void;
type MetaLayoutGetter = () => GridStackWidget[];

let registeredLayoutMetaSync: LayoutMetaSync | undefined;
let registeredMetaLayoutGetter: MetaLayoutGetter | undefined;

/**
 * Registers a callback that mirrors layout `content` into GridStack's in-memory meta map.
 * `GridStackProvider` registers on mount so widget and configure-panel saves stay in sync
 * even when callers are outside React context.
 */
export function registerDashboardLayoutMetaMapSync(sync: LayoutMetaSync | undefined): void {
  registeredLayoutMetaSync = sync;
}

/**
 * Registers a snapshot of GridStack's in-memory widget meta map.
 * `useSyncDataStreamKeysFromLayout` merges this over Redux layout so `set_request_list`
 * reflects toolbar edits before the project layout state catches up.
 */
export function registerDashboardMetaLayoutGetter(getter: MetaLayoutGetter | undefined): void {
  registeredMetaLayoutGetter = getter;
}

/** Redux project layout with GridStack meta winning per widget id (for streaming sync). */
export function getMergedDashboardLayoutForStreaming(projectLayout: GridStackWidget[]): GridStackWidget[] {
  return mergeDashboardWidgetLayouts(projectLayout, registeredMetaLayoutGetter?.() ?? []);
}

/**
 * Copies widget `content` from a layout into the raw meta map (position/size unchanged).
 */
export function syncRawWidgetMetaMapFromLayout(setMap: Dispatch<SetStateAction<Map<string, GridStackWidget>>>, layout: GridStackWidget[]): void {
  setMap((prev) => {
    let newMap: Map<string, GridStackWidget> | null = null;

    for (const widget of layout) {
      if (!widget.id || !widget.content) continue;
      const existing = prev.get(widget.id);
      if (!existing || existing.content === widget.content) continue;
      if (!newMap) newMap = new Map(prev);
      newMap.set(widget.id, { ...existing, content: widget.content });
    }

    return newMap ?? prev;
  });
}

/** Persists dashboard layout to Redux and keeps GridStack meta map in sync when registered. */
export function persistDashboardLayoutToProject(dispatch: AppDispatch, nextLayout: GridStackWidget[]): void {
  registeredLayoutMetaSync?.(nextLayout);
  dispatch(projectSlice.actions.onUpdateLastModified(new Date().toISOString()));
  dispatch(projectSlice.actions.onSaveLayout(nextLayout));
}

/**
 * Applies a content merge to one widget in a dashboard layout array.
 * Returns null when the widget is missing or has no parseable content.
 */
export function updateWidgetInDashboardLayout(
  layout: GridStackWidget[],
  widgetId: string,
  merge: (content: DashboardWidgetContent) => DashboardWidgetContent
): GridStackWidget[] | null {
  if (!layout.some((widget) => widget.id === widgetId && widget.content)) return null;

  return layout
    .map((widget) => {
      if (widget.id !== widgetId || !widget.content) return widget;
      const content = parseDashboardWidgetContent(widget.content);
      if (!content) return widget;
      return { ...widget, content: JSON.stringify(merge(content)) };
    })
    .filter((w): w is GridStackWidget => Boolean(w));
}

/**
 * Keeps GridStack's in-memory widget metadata in sync when widget content changes.
 * GridStackRender reads from this map; grid move/resize persists it back to Redux.
 */
export function updateRawWidgetMetaMapContent(
  setMap: Dispatch<SetStateAction<Map<string, GridStackWidget>>>,
  widgetId: string,
  merge: (content: DashboardWidgetContent) => DashboardWidgetContent
): void {
  setMap((prev) => {
    const widget = prev.get(widgetId);
    if (!widget?.content) return prev;
    const content = parseDashboardWidgetContent(widget.content);
    if (!content) return prev;

    const newMap = new Map(prev);
    newMap.set(widgetId, { ...widget, content: JSON.stringify(merge(content)) });
    return newMap;
  });
}
